const featured = {
  pillar: "Product Sourcing",
  pillarIcon: "ri-search-eye-line",
  quote:
    "We were skeptical at first — we'd been burned by sourcing brokers before. But LTF Sourcing came through with three solid supplier options within a couple of days. We went with one, and the first order went smoothly. Still early days, but it's a promising start.",
  name: "Marcus Chen",
  role: "Operations Manager",
  company: "NexaTech",
  avatar: "https://public.readdy.ai/ai/img_res/683ab18ed4139fa4dc028554b26754d7.jpg",
  stat1: { value: "3 days", label: "First Supplier Match" },
  stat2: { value: "~18%", label: "Cost Reduction on First Order" },
};

const testimonials = [
  {
    pillar: "Wholesale Supply",
    pillarIcon: "ri-store-3-line",
    pillarColor: "text-[#3A506B]",
    pillarBg: "bg-[#D9E2EC]",
    quote:
      "We placed our first wholesale order through LTF Sourcing about two months ago. Communication was clear, delivery was on time, and the pricing was better than what we were getting before. We're planning to use them again for our next replenishment cycle.",
    name: "James Osei",
    role: "Purchasing Lead",
    company: "Merida Goods",
    avatar: "https://readdy.ai/api/search-image?query=professional%20african%20male%20purchasing%20manager%20headshot%20portrait%20neutral%20background%20confident%20smile%20corporate%20photography%20studio%20lighting&width=64&height=64&seq=avatar-005&orientation=squarish",
    stars: 5,
  },
  {
    pillar: "Inventory Solutions",
    pillarIcon: "ri-archive-drawer-line",
    pillarColor: "text-[#C9A227]",
    pillarBg: "bg-[#C9A227]/10",
    quote:
      "Had a batch of slow-moving inventory I wasn't sure what to do with. LTF Sourcing helped connect us with a buyer and we moved most of it within a few weeks. It wasn't instant, but it worked — and it freed up some much-needed warehouse space.",
    name: "Rachel Torres",
    role: "Inventory Coordinator",
    company: "ClearPath Retail",
    avatar: "https://readdy.ai/api/search-image?query=professional%20latina%20female%20inventory%20coordinator%20headshot%20portrait%20neutral%20background%20confident%20smile%20corporate%20photography%20studio%20lighting&width=64&height=64&seq=avatar-006&orientation=squarish",
    stars: 4,
  },
  {
    pillar: "Product Sourcing",
    pillarIcon: "ri-links-line",
    pillarColor: "text-[#C9A227]",
    pillarBg: "bg-yellow-50",
    quote:
      "I reached out to LTF Sourcing after struggling to find a reliable supplier on my own. They were responsive, walked me through the process, and helped me get a sample order placed. It's early, but I'm happy with how things have gone so far.",
    name: "Emily Zhang",
    role: "Small Business Owner",
    company: "Zhang Home Goods",
    avatar: "https://readdy.ai/api/search-image?query=professional%20chinese%20female%20small%20business%20owner%20headshot%20portrait%20neutral%20background%20confident%20smile%20corporate%20photography%20studio%20lighting&width=64&height=64&seq=avatar-004&orientation=squarish",
    stars: 4,
  },
];

const stats = [
  { value: "75+", label: "Clients Served" },
  { value: "150+", label: "Products Sourced" },
  { value: "12", label: "Supplier Partners" },
  { value: "8", label: "Countries Reached" },
];

export default function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3">
            Testimonials
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#222831] leading-tight">
            What Our Early Clients Are Saying
          </h2>
          <p className="mt-4 text-base text-[#3A506B] max-w-lg leading-relaxed">
            We&apos;re just getting started — here&apos;s honest feedback from the first businesses
            that trusted LTF Sourcing to help them.
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="relative rounded-3xl bg-[#222831] p-10 lg:p-14 mb-8 overflow-hidden">
          <div className="absolute top-6 right-10 text-[180px] font-serif text-white/5 leading-none select-none">"</div>
          <div className="relative z-10 flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1">
              {/* Pillar badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/40 mb-5">
                <i className={`${featured.pillarIcon} text-[#C9A227] text-xs`}></i>
                <span className="text-[#C9A227] text-xs font-bold uppercase tracking-widest">
                  {featured.pillar}
                </span>
              </div>
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C9A227] text-lg"></i>
                ))}
              </div>
              <p className="text-xl lg:text-2xl text-white font-light leading-relaxed italic">
                &ldquo;{featured.quote}&rdquo;
              </p>
              <div className="flex items-center gap-4 mt-8">
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#C9A227]">
                  <img
                    src={featured.avatar}
                    alt={featured.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div>
                  <p className="text-white font-bold text-base">{featured.name}</p>
                  <p className="text-white/50 text-sm">{featured.role}, {featured.company}</p>
                </div>
              </div>
            </div>
            <div className="lg:w-56 flex-shrink-0 flex flex-col gap-4">
              <div className="rounded-xl bg-white/10 p-4">
                <p className="text-3xl font-extrabold text-[#C9A227]">{featured.stat1.value}</p>
                <p className="text-white/60 text-sm mt-1">{featured.stat1.label}</p>
              </div>
              <div className="rounded-xl bg-white/10 p-4">
                <p className="text-3xl font-extrabold text-[#C9A227]">{featured.stat2.value}</p>
                <p className="text-white/60 text-sm mt-1">{featured.stat2.label}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl border border-[#D9E2EC] bg-[#D9E2EC] p-6 flex flex-col gap-4">
              {/* Pillar badge */}
              <div className={`self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${t.pillarBg} border border-current/10`}>
                <i className={`${t.pillarIcon} text-xs ${t.pillarColor}`}></i>
                <span className={`text-xs font-bold uppercase tracking-widest ${t.pillarColor}`}>
                  {t.pillar}
                </span>
              </div>
              <div className="flex gap-1">
                {[...Array(t.stars)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C9A227] text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-[#3A506B] leading-relaxed italic flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-2 border-t border-[#c4d0de]">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
                </div>
                <div>
                  <p className="text-[#222831] font-bold text-sm">{t.name}</p>
                  <p className="text-[#3A506B] text-xs">{t.role}, {t.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="rounded-2xl bg-[#C9A227] p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold text-white">{stat.value}</span>
              <span className="text-white/80 text-sm mt-1">{stat.label}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
